import type { AppListenerEffectAPI, AppStartListening } from '@/redux/store';
import { Action, createListenerMiddleware, isRejected } from '@reduxjs/toolkit';
import { createErrorData } from '@/utils/error-report/errorReporting';
import { openAccessDeniedModal, openErrorReportModal } from '@/redux/modal/modal.slice';
import {
  CONFLICT_AXIOS_ERROR_CODE,
  CONNECTION_ERROR_MESSAGE,
  CONNECTION_ERROR_CODES,
  FORBIDDEN_AXIOS_ERROR_CODE,
  UNAUTHORIZED_AXIOS_ERROR_CODE,
} from '@/utils/getErrorMessage/getErrorMessage.constants';
import { showToast } from '@/utils/showToast';

const CONNECTION_ERROR_TOAST_ID = 'connection-error';

export const errorListenerMiddleware = createListenerMiddleware();

const startListening = errorListenerMiddleware.startListening as AppStartListening;

export const errorMiddlewareListener = async (
  action: Action,
  { getState, dispatch }: AppListenerEffectAPI,
): Promise<void> => {
  if (isRejected(action) && action.type !== 'user/restoreSession/rejected') {
    if (action.error.code === UNAUTHORIZED_AXIOS_ERROR_CODE) {
      // 401 means the token is dead. The axios layer already refreshed it or dropped the
      // session (which opens the login modal), so there is nothing to report to the user here.
    } else if (action.error.code === FORBIDDEN_AXIOS_ERROR_CODE) {
      // 403 means "authenticated, but not allowed" - never a token problem, so the session is
      // left alone. The project list is deliberately not re-fetched here: this listener runs on
      // every rejected action, so re-fetching it on a 403 made a rejected project list request
      // repeat itself in an endless loop.
      dispatch(openAccessDeniedModal());
    } else if (action.error.code && CONNECTION_ERROR_CODES.includes(action.error.code)) {
      showToast({
        type: 'error',
        message: CONNECTION_ERROR_MESSAGE,
        duration: 15000,
        id: CONNECTION_ERROR_TOAST_ID,
      });
    } else if (action.error.code === CONFLICT_AXIOS_ERROR_CODE) {
      showToast({
        type: 'error',
        message:
          'There was a problem with updating data. This item was modified by someone else. ' +
          'You can try again. If you think it\'s an error, <a href="mailto:minerva@uni.lu" class="underline"> contact developers' +
          '</a>).',
        duration: 15000,
      });
    } else if (action.error.name === 'ValidationError') {
      showToast({
        type: 'error',
        message: action.error.message || 'ValidationError',
        duration: 15000,
      });
    } else {
      const errorData = await createErrorData(action.error, getState());
      // eslint-disable-next-line no-console
      console.log(errorData);
      dispatch(openErrorReportModal(errorData));
    }
  }
};

startListening({
  matcher: isRejected,
  effect: errorMiddlewareListener,
});
